import { Bike, ExternalLink } from 'lucide-react';

import type { RouteLeg } from '@/api/types';
import { formatLegDistance, formatLegDuration, formatLegTime } from '@/lib/route';
import { cn } from '@/lib/utils';
import { Card } from './ui/card';

/**
 * How long the trip is, shop to door, in one line a rider can read at a glance.
 *
 * Numbers first, because "how far, how long" is the question — a pin on a map
 * does not say whether this is 800 m or 4 km. Sits between the two ends of the
 * leg on the delivery screen, which is where the trip is in real life.
 *
 * An estimate from the two saved pins, and it says so. The exact route is one
 * tap away: once the door is known the whole card opens it in Google Maps,
 * drawn from the pharmacy. Before pickup the destination is withheld, so there
 * is nothing to link to and the card only informs.
 */
export function RouteSummary({
  route,
  href,
  className,
}: {
  route: RouteLeg | null | undefined;
  /** The Maps deeplink for the whole leg. Omitted while the door is withheld. */
  href?: string | null;
  className?: string;
}) {
  if (!route) return null;

  const distance = formatLegDistance(route);
  const duration = formatLegDuration(route);
  const time = formatLegTime(route);

  const body = (
    <div className="flex items-center gap-3 p-4">
      <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-wash text-brand">
        <Bike className="size-5" aria-hidden="true" />
      </span>

      <div className="min-w-0 flex-1">
        {/*
         * One "about" for both numbers, and neither number allowed to break
         * across a line — "about / 4 min" on a narrow phone reads as two
         * unrelated fragments.
         */}
        <p data-numeric className="text-xl font-bold leading-tight tracking-tight text-ink">
          <span className="text-base font-semibold text-ink-muted">About </span>
          <span className="whitespace-nowrap">{distance}</span>
          <span className="font-semibold text-ink-muted"> · </span>
          <span className="whitespace-nowrap">{time}</span>
        </p>
        <p className="mt-0.5 text-sm leading-snug text-ink-subtle">
          {href ? 'Shop to door. Tap for the exact route in Maps.' : 'Shop to door, estimated'}
        </p>
      </div>

      {href ? (
        <span className="flex shrink-0 items-center gap-1 text-sm font-semibold text-brand">
          Route
          <ExternalLink className="size-4" aria-hidden="true" />
        </span>
      ) : null}
    </div>
  );

  if (!href) {
    return (
      <Card variant="wash" className={className}>
        {body}
      </Card>
    );
  }

  return (
    <Card asChild variant="wash" interactive className={cn('block', className)}>
      {/*
       * A new tab, like every maps link in this app: navigating away mid-leg
       * would cost the rider the screen they are working from.
       */}
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`About ${distance}, ${duration} from the shop to the door. Open the exact route in Maps.`}
        className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
      >
        {body}
      </a>
    </Card>
  );
}
